import {
  ActionIcon,
  Badge,
  Box,
  Card,
  CopyButton,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCircleCheck,
  IconCircleDashed,
  IconClockHour4,
  IconCopy,
  IconPackage,
  IconTruckDelivery,
} from "@tabler/icons-react";
import type { CSSProperties } from "react";
import { Link } from "react-router";
import {
  SAMPLE_PACK_LABELS,
  SAMPLE_STATUS_LABELS,
  SAMPLE_STATUSES,
  type ClientSampleDto,
  type SampleStatus,
} from "@mexp/shared";
import { dayMonth } from "./stage";

/** The sample stages in the portal's stage hues: waiting, being made, on its way, arrived. */
export const SAMPLE_COLOR: Record<SampleStatus, string> = {
  requested: "gray",
  in_preparation: "brand",
  dispatched: "violet",
  delivered: "teal",
};

const vars = (status: SampleStatus) =>
  ({
    "--status-fill": `var(--mantine-color-${SAMPLE_COLOR[status]}-filled)`,
    "--status-text": `var(--mantine-color-${SAMPLE_COLOR[status]}-light-color)`,
    "--status-soft": `var(--mantine-color-${SAMPLE_COLOR[status]}-light)`,
  }) as CSSProperties;

const day = (iso: string | null) => (iso ? dayMonth(iso.slice(0, 10)) : null);

/** The four stages as one track, like the dossier's: when each was reached, and the expected delivery. */
function SampleTrack({ s }: { s: ClientSampleDto }) {
  const current = SAMPLE_STATUSES.indexOf(s.status);
  const done = s.status === "delivered";
  return (
    <ol className="portal-status-track" aria-label="Sample stages">
      {SAMPLE_STATUSES.map((stage, i) => {
        const state =
          done || i < current ? "done" : i === current ? "current" : "next";
        const caption =
          state !== "next"
            ? day(s.stageDates[stage])
            : stage === "delivered" && s.expectedDelivery
              ? `Expected ${dayMonth(s.expectedDelivery)}`
              : null;
        const Icon =
          state === "done"
            ? IconCircleCheck
            : state === "current"
              ? IconClockHour4
              : IconCircleDashed;
        return (
          <li
            key={stage}
            className="portal-status-step"
            data-state={state}
            aria-current={state === "current" ? "step" : undefined}
          >
            <span className="portal-status-bar" aria-hidden>
              <span style={{ width: state === "next" ? 0 : "100%" }} />
            </span>
            <Group gap={6} wrap="nowrap" mt={10}>
              <Icon
                size={16}
                stroke={1.8}
                className="portal-status-icon"
                aria-hidden
              />
              <Text
                fz="sm"
                fw={state === "current" ? 650 : 500}
                className="portal-status-label"
              >
                {SAMPLE_STATUS_LABELS[stage]}
              </Text>
            </Group>
            {caption && (
              <Text fz="xs" c="dimmed" className="tabular" pl={22}>
                {caption}
              </Text>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The courier and the tracking number to follow the parcel with (copyable). */
function Courier({ s }: { s: ClientSampleDto }) {
  if (!s.courierName && !s.trackingNo) return null;
  return (
    <Group
      gap="sm"
      wrap="nowrap"
      align="flex-start"
      className="portal-sample-courier"
    >
      <IconTruckDelivery
        size={22}
        stroke={1.6}
        color="var(--status-text)"
        style={{ flex: "none", marginTop: 2 }}
        aria-hidden
      />
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text fz="xs" c="dimmed">
          {s.courierName ?? "Courier"}
        </Text>
        {s.trackingNo && (
          <Group gap={4} wrap="nowrap">
            <Text
              fz="md"
              fw={650}
              ff="monospace"
              className="tabular"
              style={{ overflowWrap: "anywhere" }}
            >
              {s.trackingNo}
            </Text>
            <CopyButton value={s.trackingNo} timeout={1600}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied" : "Copy the tracking number"}
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color={copied ? "teal" : "gray"}
                    size="sm"
                    onClick={copy}
                    aria-label="Copy the tracking number"
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        )}
      </Stack>
    </Group>
  );
}

/** The sample asked for with a requisition, on its page: where it stands and how to follow it. */
export function SampleCard({ sample: s }: { sample: ClientSampleDto }) {
  return (
    <Card
      p={{ base: "md", sm: "xl" }}
      style={vars(s.status)}
      aria-label="Sample"
    >
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="lg">
          <Group
            gap="md"
            wrap="nowrap"
            align="flex-start"
            style={{ flex: "1 1 320px", minWidth: 0 }}
          >
            <IconPackage
              size={28}
              stroke={1.5}
              color="var(--status-text)"
              style={{ flex: "none", marginTop: 2 }}
              aria-hidden
            />
            <Group gap={8} wrap="wrap" mt={4}>
              <Text fz="sm" fw={600}>
                {SAMPLE_PACK_LABELS[s.packType]}
              </Text>
              <Badge color={SAMPLE_COLOR[s.status]} variant="light">
                {SAMPLE_STATUS_LABELS[s.status]}
              </Badge>
            </Group>
          </Group>
          <Courier s={s} />
        </Group>
        <SampleTrack s={s} />
      </Stack>
    </Card>
  );
}

/** One sample in the Samples list: the product, where the sample stands, and the courier once it ships. */
export function SampleRow({ s }: { s: ClientSampleDto }) {
  const current = SAMPLE_STATUSES.indexOf(s.status);
  const shipped = s.status === "dispatched";
  return (
    <Box
      component={Link}
      to={`/requests/${s.requestId}`}
      className="portal-row"
      style={vars(s.status)}
    >
      <Group justify="space-between" wrap="wrap" gap="sm" align="center">
        <Stack gap={2} style={{ minWidth: 0, flex: "1 1 260px" }}>
          <Text fw={600} truncate="end">
            {s.productName}{" "}
            <Text span c="dimmed" fw={400} inherit>
              for {s.country}
            </Text>
          </Text>
          <Text fz="xs" c="dimmed" className="tabular">
            {s.code} · {SAMPLE_PACK_LABELS[s.packType]}
            {shipped && s.courierName ? ` · ${s.courierName}` : ""}
            {shipped && s.trackingNo ? ` ${s.trackingNo}` : ""}
          </Text>
        </Stack>
        <Group gap="md" wrap="nowrap" style={{ flex: "none" }}>
          <Group gap={3} wrap="nowrap" aria-hidden>
            {SAMPLE_STATUSES.map((stage, i) => (
              <span
                key={stage}
                className="portal-sample-pip"
                data-on={i <= current || undefined}
              />
            ))}
          </Group>
          <Stack gap={0} align="flex-end" miw={112}>
            <Badge color={SAMPLE_COLOR[s.status]} variant="light">
              {SAMPLE_STATUS_LABELS[s.status]}
            </Badge>
            <Text fz="xs" c="dimmed" className="tabular" mt={2}>
              {s.status === "dispatched" && s.expectedDelivery
                ? `Expected ${dayMonth(s.expectedDelivery)}`
                : (day(s.stageDates[s.status]) ?? "")}
            </Text>
          </Stack>
        </Group>
      </Group>
    </Box>
  );
}
